import React, { useState, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { SiteSettings } from '../../types';
import {
  Smartphone,
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Save,
  Copy,
  Check,
  RefreshCw,
  Server,
  Zap,
  Info,
  Sliders,
  DollarSign,
  Clock,
  Lock,
} from 'lucide-react';

export const DanaPaymentEditor: React.FC = () => {
  const { data, saveData, showToast } = useContent();

  // Settings from data.siteSettings with safe defaults
  const siteSettings: Partial<SiteSettings> = data.siteSettings || {};
  
  const [danaEnabled, setDanaEnabled] = useState<boolean>(
    siteSettings.danaEnabled ?? true
  );
  const [merchantName, setMerchantName] = useState<string>(
    siteSettings.danaMerchantName || 'LetonKu Official'
  );
  const [minAmount, setMinAmount] = useState<number>(
    siteSettings.danaMinAmount ?? 10000
  );
  const [maxAmount, setMaxAmount] = useState<number>(
    siteSettings.danaMaxAmount ?? 2000000
  );
  const [instructions, setInstructions] = useState<string>(
    siteSettings.danaInstructions ||
      'Pesanan dengan pembayaran DANA akan diverifikasi secara otomatis oleh sistem gateway SNAP BI begitu pembayaran berhasil diselesaikan di aplikasi DANA.'
  );

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Sync state when data updates from server
  useEffect(() => {
    if (siteSettings) {
      setDanaEnabled(siteSettings.danaEnabled ?? true);
      setMerchantName(siteSettings.danaMerchantName || 'LetonKu Official');
      setMinAmount(siteSettings.danaMinAmount ?? 10000);
      setMaxAmount(siteSettings.danaMaxAmount ?? 2000000);
      setInstructions(
        siteSettings.danaInstructions ||
          'Pesanan dengan pembayaran DANA akan diverifikasi secara otomatis oleh sistem gateway SNAP BI begitu pembayaran berhasil diselesaikan di aplikasi DANA.'
      );
      setHasUnsavedChanges(false);
    }
  }, [data.siteSettings]);

  const handleCopy = (text: string, fieldId: string) => {
    try {
      navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      showToast('Berhasil disalin ke clipboard', 'success');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      showToast('Gagal menyalin text', 'error');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (minAmount < 0) {
      showToast('Batas minimum transaksi tidak boleh kurang dari 0', 'error');
      return;
    }
    if (maxAmount <= minAmount) {
      showToast('Batas maksimum transaksi harus lebih besar dari batas minimum', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const updatedSiteSettings = {
        ...data.siteSettings,
        danaEnabled,
        danaMerchantName: merchantName.trim(),
        danaMinAmount: Number(minAmount),
        danaMaxAmount: Number(maxAmount),
        danaInstructions: instructions.trim(),
      };

      const success = await saveData({
        ...data,
        siteSettings: updatedSiteSettings,
      });

      if (success) {
        showToast('Pengaturan Pembayaran DANA berhasil disimpan!', 'success');
        setHasUnsavedChanges(false);
      } else {
        showToast('Gagal menyimpan pengaturan ke database', 'error');
      }
    } catch (err: any) {
      showToast('Terjadi kesalahan: ' + (err?.message || String(err)), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setDanaEnabled(siteSettings.danaEnabled ?? true);
    setMerchantName(siteSettings.danaMerchantName || 'LetonKu Official');
    setMinAmount(siteSettings.danaMinAmount ?? 10000);
    setMaxAmount(siteSettings.danaMaxAmount ?? 2000000);
    setInstructions(
      siteSettings.danaInstructions ||
        'Pesanan dengan pembayaran DANA akan diverifikasi secara otomatis oleh sistem gateway SNAP BI begitu pembayaran berhasil diselesaikan di aplikasi DANA.'
    );
    setHasUnsavedChanges(false);
    showToast('Form telah dikembalikan ke pengaturan terakhir yang tersimpan', 'info');
  };

  const formatRupiah = (num: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(num);
  };

  const currentHost = typeof window !== 'undefined' ? window.location.origin : 'https://letoncoffee.com';
  const notifyUrl = `${currentHost}/v1.0/debit/notify`;
  const returnUrl = `${currentHost}/v1.0/debit/status.htm`;
  const b2bTokenUrl = `${currentHost}/v1.0/access-token/b2b`;

  return (
    <div className="space-y-8 max-w-5xl mx-auto pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-[#E0F2FE]">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-[#E0F2FE] text-[#0284C7] uppercase tracking-wider">
              GATEWAY PEMBAYARAN PUSAT
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              SNAP BI v1.0
            </span>
          </div>
          <h2 className="text-2xl font-display font-black text-[#172033] tracking-wide uppercase flex items-center gap-2.5">
            <Smartphone className="w-6 h-6 text-[#0284C7]" />
            Pengaturan Pembayaran DANA
          </h2>
          <p className="text-xs text-[#64748B] mt-1">
            Konfigurasi gateway pembayaran DANA Hosted Checkout berstandar Bank Indonesia SNAP BI untuk LetonKu.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
              <AlertCircle className="w-3.5 h-3.5" />
              Ada perubahan belum disimpan
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0284C7] hover:bg-[#0369a1] text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all cursor-pointer disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{isSaving ? 'Menyimpan...' : 'Simpan Pengaturan'}</span>
          </button>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Toggle Switch Card */}
        <div className="bg-white rounded-2xl border border-[#E0F2FE] p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-[#172033]">
                  Status Pembayaran DANA
                </h3>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${
                    danaEnabled
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}
                >
                  {danaEnabled ? 'AKTIF (ONLINE)' : 'NONAKTIF (OFFLINE)'}
                </span>
              </div>
              <p className="text-xs text-[#64748B] max-w-xl">
                Aktifkan opsi metode pembayaran DANA Hosted Checkout pada halaman pemesanan customer di seluruh cabang LetonKu.
              </p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={danaEnabled}
                onChange={(e) => {
                  setDanaEnabled(e.target.checked);
                  setHasUnsavedChanges(true);
                }}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-slate-200 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#0284C7]"></div>
            </label>
          </div>
        </div>

        {/* Operational Parameters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card: Transaksi & Limits */}
          <div className="bg-white rounded-2xl border border-[#E0F2FE] p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-2 pb-3 border-b border-[#F0F7FF]">
              <Sliders className="w-4 h-4 text-[#0284C7]" />
              <h3 className="text-sm font-bold text-[#172033] uppercase tracking-wider">
                Parameter & Batas Transaksi
              </h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#172033] uppercase tracking-wider mb-1.5">
                  Label Nama Merchant di DANA
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={merchantName}
                    onChange={(e) => {
                      setMerchantName(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Contoh: LetonKu Official"
                    className="w-full px-3.5 py-2.5 bg-[#F8FBFF] border border-[#E0F2FE] rounded-xl text-xs text-[#172033] font-medium focus:outline-hidden focus:border-[#0284C7] focus:bg-white transition-all"
                  />
                </div>
                <p className="text-[11px] text-[#64748B] mt-1">
                  Nama outlet atau brand yang tertera pada layar otorisasi DANA customer.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#172033] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <DollarSign className="w-3.5 h-3.5 text-[#0284C7]" />
                    Min. Transaksi
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    value={minAmount}
                    onChange={(e) => {
                      setMinAmount(Number(e.target.value));
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 bg-[#F8FBFF] border border-[#E0F2FE] rounded-xl text-xs text-[#172033] font-mono font-semibold focus:outline-hidden focus:border-[#0284C7] focus:bg-white"
                  />
                  <span className="text-[10px] text-[#64748B] block mt-1">
                    {formatRupiah(minAmount)}
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#172033] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <DollarSign className="w-3.5 h-3.5 text-[#0284C7]" />
                    Maks. Transaksi
                  </label>
                  <input
                    type="number"
                    min={minAmount}
                    step={10000}
                    value={maxAmount}
                    onChange={(e) => {
                      setMaxAmount(Number(e.target.value));
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 bg-[#F8FBFF] border border-[#E0F2FE] rounded-xl text-xs text-[#172033] font-mono font-semibold focus:outline-hidden focus:border-[#0284C7] focus:bg-white"
                  />
                  <span className="text-[10px] text-[#64748B] block mt-1">
                    {formatRupiah(maxAmount)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#172033] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-[#0284C7]" />
                  Masa Berlaku Pembayaran (Timeout)
                </label>
                <div className="p-3 rounded-xl bg-[#F0F7FF] border border-[#E0F2FE] text-xs text-[#172033] flex items-center justify-between">
                  <span className="font-medium">Batas Waktu Selesai Bayar</span>
                  <span className="font-mono font-bold text-[#0284C7] bg-white px-2 py-0.5 rounded-md border border-[#E0F2FE]">
                    15 Menit
                  </span>
                </div>
                <p className="text-[11px] text-[#64748B] mt-1">
                  Pesanan otomatis expired bila otorisasi saldo DANA tidak selesai dalam kurun 15 menit.
                </p>
              </div>
            </div>
          </div>

          {/* Card: Instruksi Operasional */}
          <div className="bg-white rounded-2xl border border-[#E0F2FE] p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-2 pb-3 border-b border-[#F0F7FF]">
              <Info className="w-4 h-4 text-[#0284C7]" />
              <h3 className="text-sm font-bold text-[#172033] uppercase tracking-wider">
                Instruksi Operasional Barista & Kasir
              </h3>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-[#172033] uppercase tracking-wider mb-1.5">
                  Catatan untuk Tampilan Pesanan
                </label>
                <textarea
                  rows={4}
                  value={instructions}
                  onChange={(e) => {
                    setInstructions(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="Instruksi operasional untuk kasir dan barista..."
                  className="w-full px-3.5 py-2.5 bg-[#F8FBFF] border border-[#E0F2FE] rounded-xl text-xs text-[#172033] font-medium focus:outline-hidden focus:border-[#0284C7] focus:bg-white transition-all leading-relaxed"
                />
              </div>

              <div className="p-3.5 rounded-xl bg-sky-50/60 border border-sky-100 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#0284C7]">
                  <Zap className="w-3.5 h-3.5" />
                  Keunggulan Verifikasi Otomatis
                </div>
                <ul className="text-[11px] text-[#475569] space-y-1 list-disc list-inside">
                  <li>Tidak memerlukan upload bukti struk pembayaran manual.</li>
                  <li>Notifikasi pesanan berbunyi seketika setelah DANA memotong saldo.</li>
                  <li>Nomor referensi mitra (Partner Reference) langsung dicocokkan ke database.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Integration Specs (Audit & Webhook Config) */}
        <div className="bg-white rounded-2xl border border-[#E0F2FE] p-6 shadow-sm space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 border-b border-[#F0F7FF]">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-[#F0F7FF] text-[#0284C7]">
                <Server className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#172033] uppercase tracking-wider">
                  Konfigurasi Endpoint & Spesifikasi Gateway SNAP BI
                </h3>
                <p className="text-xs text-[#64748B]">
                  Gunakan URL berikut pada Developer Portal DANA Dashboard untuk pengaturan Webhook & Return URL.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="w-3.5 h-3.5" />
                RSA-SHA256 & HMAC-SHA512
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Notification Webhook */}
            <div className="p-4 rounded-xl bg-[#F8FBFF] border border-[#E0F2FE] space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono font-bold uppercase text-[#0284C7]">
                  WEBHOOK NOTIFICATION (SERVICE 73)
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(notifyUrl, 'notify')}
                  className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0284C7] hover:bg-white transition-all cursor-pointer"
                  title="Salin URL"
                >
                  {copiedField === 'notify' ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <p className="text-xs font-mono font-semibold text-[#172033] break-all select-all bg-white p-2 rounded-lg border border-[#E0F2FE]">
                {notifyUrl}
              </p>
              <p className="text-[10px] text-[#64748B]">
                Endpoint yang dipanggil DANA secara instan saat transaksi debit berhasil / gagal.
              </p>
            </div>

            {/* Return URL */}
            <div className="p-4 rounded-xl bg-[#F8FBFF] border border-[#E0F2FE] space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono font-bold uppercase text-[#0284C7]">
                  REDIRECT RETURN URL (PAY RETURN)
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(returnUrl, 'return')}
                  className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0284C7] hover:bg-white transition-all cursor-pointer"
                  title="Salin URL"
                >
                  {copiedField === 'return' ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <p className="text-xs font-mono font-semibold text-[#172033] break-all select-all bg-white p-2 rounded-lg border border-[#E0F2FE]">
                {returnUrl}
              </p>
              <p className="text-[10px] text-[#64748B]">
                Halaman redirect otomatis yang membawa customer kembali ke status pesanan LetonKu.
              </p>
            </div>

            {/* B2B Token Endpoint */}
            <div className="p-4 rounded-xl bg-[#F8FBFF] border border-[#E0F2FE] space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono font-bold uppercase text-[#0284C7]">
                  OAUTH B2B ACCESS TOKEN
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(b2bTokenUrl, 'b2b')}
                  className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0284C7] hover:bg-white transition-all cursor-pointer"
                  title="Salin URL"
                >
                  {copiedField === 'b2b' ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <p className="text-xs font-mono font-semibold text-[#172033] break-all select-all bg-white p-2 rounded-lg border border-[#E0F2FE]">
                {b2bTokenUrl}
              </p>
              <p className="text-[10px] text-[#64748B]">
                Penyedia token otorisasi server-to-server asimetris dengan RSA-SHA256.
              </p>
            </div>

            {/* Security Architecture */}
            <div className="p-4 rounded-xl bg-[#F8FBFF] border border-[#E0F2FE] space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono font-bold uppercase text-[#0284C7] flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  PROTOKOL KEAMANAN SNAP BI
                </span>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  SECURE
                </span>
              </div>
              <div className="text-xs text-[#172033] space-y-1 font-medium bg-white p-2 rounded-lg border border-[#E0F2FE]">
                <p>• Signature: Asymmetric RSA PKCS#8 / SPKI</p>
                <p>• Payload Integrity: SHA-256 Digest</p>
                <p>• Zero Plaintext Secrets in Client Browser</p>
              </div>
              <p className="text-[10px] text-[#64748B]">
                Seluruh transaksi dan validasi webhook berjalan sepenuhnya di lingkungan server-side yang terisolasi.
              </p>
            </div>
          </div>
        </div>

        {/* Operational Flow SOP */}
        <div className="bg-[#F0F7FF] rounded-2xl border border-[#E0F2FE] p-6 space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-white text-[#0284C7] shadow-xs">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-[#172033] uppercase tracking-wider">
                Alur Kerja Pesanan DANA (Standard Operating Procedure)
              </h4>
              <p className="text-xs text-[#64748B]">
                Bagaimana pesanan DANA diproses dari customer sampai ke printer & layar kasir outlet.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
            <div className="p-3.5 rounded-xl bg-white border border-[#E0F2FE] space-y-1">
              <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-bold flex items-center justify-center text-xs">
                1
              </div>
              <p className="font-bold text-[#172033]">Checkout Customer</p>
              <p className="text-[11px] text-[#64748B] leading-tight">
                Customer memilih opsi DANA di halaman checkout dan diarahkan ke gateway SNAP BI.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-white border border-[#E0F2FE] space-y-1">
              <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-bold flex items-center justify-center text-xs">
                2
              </div>
              <p className="font-bold text-[#172033]">Otorisasi Saldo</p>
              <p className="text-[11px] text-[#64748B] leading-tight">
                Customer mengonfirmasi pembayaran dengan PIN atau biometrik di aplikasi DANA.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-white border border-[#E0F2FE] space-y-1">
              <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-bold flex items-center justify-center text-xs">
                3
              </div>
              <p className="font-bold text-[#172033]">Webhook Otomatis</p>
              <p className="text-[11px] text-[#64748B] leading-tight">
                DANA memanggil server LetonKu, status pesanan otomatis berubah jadi <strong className="text-emerald-700">PAID</strong>.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-white border border-[#E0F2FE] space-y-1">
              <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-bold flex items-center justify-center text-xs">
                4
              </div>
              <p className="font-bold text-[#172033]">Pesanan Diproses</p>
              <p className="text-[11px] text-[#64748B] leading-tight">
                Notifikasi berbunyi di dashboard outlet, barista langsung menyiapkan minuman tanpa menunggu konfirmasi manual.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-[#E0F2FE]">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-xs font-semibold text-[#64748B] hover:text-[#172033] hover:bg-[#F0F7FF] rounded-xl transition-all cursor-pointer"
          >
            Reset Form
          </button>

          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#0284C7] hover:bg-[#0369a1] text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all cursor-pointer disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{isSaving ? 'Menyimpan...' : 'Simpan Pengaturan DANA'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};
